import React, { Component } from 'react'
import { Dispatch } from 'redux'
import { connect } from 'react-redux'
import { injectIntl, WrappedComponentProps } from 'react-intl'
import getConstants from '../../constants'
import * as locationActions from 'services/Location/actions'
import * as storesActions from 'services/Stores/actions'
import * as requestActions from 'services/Request/actions'
import * as statesActions from 'services/States/actions'
import * as cmsActions from 'services/cms/actions'
import StoreSearch from 'containers/StoreSearch'
import { OnSearchThisAreaType } from 'components/StoresMap'
import Meta from 'components/Meta'
import TemporaryUnavailable from 'containers/TemporaryUnavailable'
import { selector as mapStateToProps } from './selectors'
import { getURLWithoutQuery } from '../../helpers/url'
import {
  ContentAlignment,
  Image,
} from 'shared-global-ec-uikit/components/dist/react/esm'
import './styles.css'
import { DistanceUnit, FeatureFlagKeys } from 'typings'

type PropsFromDispatch = {
  onSearchByLocation: (limit?: number) => void
  onSearchByKeyword: (searchTerm: string) => void
  onAreaChange: (areaCode?: string) => void
  onSearch: (trackSearchCallBack: (resultCount?: number) => void) => void
  onSearchThisArea: OnSearchThisAreaType
  onSearchAllStores: () => void
  onFilterUpdate: (filters: { [key: string]: boolean | string }) => void
  onLoadMore: () => void
  onClearFilters: () => void
  onStoreSelect: (storeId?: any) => void
  onClearLocation: () => void
  onClearSearchTerm: () => void
  onMarkerSelect: () => void
  onClearMarker: () => void
  clearError: () => void
  onGetStates: () => void
  onGetCMSData: () => void
}

const mapDispatchToProps = (
  dispatch: Dispatch<ActionType>,
): PropsFromDispatch => ({
  onSearchByKeyword: searchTerm =>
    dispatch(storesActions.searchTermUpdated(searchTerm)),
  onClearSearchTerm: () => dispatch(storesActions.searchTermCleared()),
  onSearchByLocation: limit => dispatch(locationActions.geoRequested(limit)),
  onAreaChange: areaCode => dispatch(storesActions.areaUpdated(areaCode)),
  onSearch: (trackSearchCallBack: (resultCount?: number) => void) => {
    dispatch(storesActions.searchRequested(trackSearchCallBack))
  },
  onSearchAllStores: () => {
    dispatch(storesActions.searchAllStoresRequested())
  },
  onFilterUpdate: filters => dispatch(storesActions.filtersUpdated(filters)),
  onSearchThisArea: ({ center, radius }) =>
    dispatch(locationActions.geoUpdated(center, radius)),
  onClearLocation: () => dispatch(locationActions.geoCleared()),
  onClearFilters: () => dispatch(storesActions.filtersCleared()),
  onLoadMore: () => dispatch(storesActions.loadMoreResults()),
  onStoreSelect: (storeId?: number) =>
    dispatch(storesActions.selectStore(storeId)),
  onMarkerSelect: (storeId?: number) =>
    dispatch(storesActions.selectMarker(storeId)),
  onClearMarker: () => dispatch(storesActions.clearMarker()),
  clearError: () => dispatch(requestActions.clearError()),
  onGetStates: () => dispatch(statesActions.StatesForRegionRequested()),
  onGetCMSData: () => dispatch(cmsActions.CMSContentRequested()),
})

type Props = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  WrappedComponentProps

const CONSTANTS = getConstants()
const GOOGLE_MAPS_API_KEY = CONSTANTS.GOOGLE_MAPS_API_KEY

class HomePage extends Component<Props> {
  state = {
    result: [],
  }
  componentDidMount() {
    const { featureFlags, onSearchByLocation, isFeatureFlagLoaded } = this.props

    if (!CONSTANTS.IS_EU) {
      this.props.onGetStates()
    }

    if (CONSTANTS.CMS_ENABLED[CONSTANTS.BRAND]) {
      this.props.onGetCMSData()
    }

    if (isFeatureFlagLoaded) {
      onSearchByLocation(
        Number(featureFlags[FeatureFlagKeys.SEARCH_PAGE_SIZE]) ||
          CONSTANTS.RESULTS_PER_PAGE,
      )
    }
  }

  componentDidUpdate(prevProps: Readonly<Props>): void {
    const { featureFlags, onSearchByLocation, isFeatureFlagLoaded } = this.props
    // will store the limit value from feature flag
    // and will be using this limit for all consecutive store API calls
    if (isFeatureFlagLoaded && !prevProps.isFeatureFlagLoaded) {
      onSearchByLocation(
        Number(featureFlags[FeatureFlagKeys.SEARCH_PAGE_SIZE]) ||
          CONSTANTS.RESULTS_PER_PAGE,
      )
    }
  }

  render() {
    const {
      intl,
      storeList,
      totalCount,
      searchTerm,
      filters,
      selectedStore,
      isSearchingByMapArea,
      onSearchByLocation,
      onSearchByKeyword,
      onAreaChange,
      onSearch,
      isSearching,
      onSearchThisArea,
      onSearchAllStores,
      onFilterUpdate,
      onLoadMore,
      onClearFilters,
      onStoreSelect,
      onMarkerSelect,
      onClearMarker,
      onClearLocation,
      onClearSearchTerm,
      hasUnavailableError,
      clearError,
      selectedMarker,
      states,
      isFetchingStates,
    } = this.props

    const intlMessages = {
      searchThisArea: intl.formatMessage({ id: 'text_search_this_area' }),
      getDirections: intl.formatMessage({ id: 'text_showdirection' }),
      distanceAway:
        CONSTANTS.DISTANCE_UNIT === DistanceUnit.MILE
          ? intl.formatMessage({ id: 'text_mile' })
          : intl.formatMessage({ id: 'text_km' }),
    }

    const getEUCountries = () => {
      return CONSTANTS.EU_COUNTRY_LIST.filter(
        country => !country.defaultTo,
      ).map(country => ({
        label: intl.formatMessage({ id: country.lid }),
        value: country.countryCode,
      }))
    }

    const { REACT_APP_ASSET_BASE_URL: ASSET_BASE_URL } = process.env

    if (hasUnavailableError) {
      return <TemporaryUnavailable componentWillUnmountCallback={clearError} />
    }

    if (isFetchingStates) {
      return (
        <ContentAlignment
          alignment="center"
          direction="column"
          element="div"
          justifyContent="center"
        >
          <div style={{ height: '52vh', width: '100px', marginTop: '30vh' }}>
            <Image
              ratio="1x1"
              alt="loader"
              src={`${ASSET_BASE_URL}/g/icons/loading-cycle-animation.svg`}
              className="loaderStyle"
            />
          </div>
        </ContentAlignment>
      )
    }

    if (
      !CONSTANTS.IS_EU &&
      !isFetchingStates &&
      Object.keys(states).length === 0
    ) {
      return <TemporaryUnavailable componentWillUnmountCallback={clearError} />
    }

    const shouldRenderSearch = !CONSTANTS.IS_EU
      ? Object.keys(states).length > 0
      : true
    return (
      <>
        <Meta
          title={intl.formatMessage({
            id: 'text_store_locator_top_meta_title',
          })}
          description={intl.formatMessage({
            id: 'text_store_locator_top_meta_description',
          })}
          canonicalUrl={getURLWithoutQuery()}
        />
        {shouldRenderSearch && (
          <StoreSearch
            apiKey={GOOGLE_MAPS_API_KEY}
            initialCenter={{
              latitude: CONSTANTS.GEO_COORDINATES.INITIAL_LATITUDE,
              longitude: CONSTANTS.GEO_COORDINATES.INITIAL_LONGITUDE,
            }}
            initialZoom={CONSTANTS.GEO_COORDINATES.INITIAL_ZOOM}
            storeList={storeList}
            areas={states}
            selectedStore={selectedStore}
            selectedMarker={selectedMarker}
            searchTerm={searchTerm}
            filters={filters}
            totalCount={totalCount}
            areaLabel={intl.formatMessage({
              id: CONSTANTS.AREA_TRANSLATION_KEY,
            })}
            allAreasLabel={intl.formatMessage({
              id: 'text_all',
            })}
            countryListEU={getEUCountries()}
            intlMessages={intlMessages}
            isSearchingByMapArea={isSearchingByMapArea}
            isSearching={isSearching}
            onSearchByLocation={onSearchByLocation}
            onKeywordChange={onSearchByKeyword}
            onAreaChange={onAreaChange}
            onSearch={onSearch}
            onSearchThisArea={onSearchThisArea}
            onFilter={onFilterUpdate}
            onClearFilters={onClearFilters}
            onLoadMore={onLoadMore}
            onStoreSelect={onStoreSelect}
            onMarkerSelect={onMarkerSelect}
            onClearMarker={onClearMarker}
            onClearLocation={onClearLocation}
            onClearSearchTerm={onClearSearchTerm}
            onSeeAllStores={onSearchAllStores}
          />
        )}
      </>
    )
  }
}

export default injectIntl(
  connect(mapStateToProps, mapDispatchToProps)(HomePage),
)
